import { useEffect, useRef, useState } from "react";

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  /** Disable on desktop (default: only mobile) */
  enabled?: boolean;
}

/**
 * Mobile pull-to-refresh gesture. Attach the returned `bind` props to a
 * scrollable container. Returns `pulling` (0..1) and `refreshing` flags so
 * callers can show an indicator.
 */
export function usePullToRefresh({
  onRefresh,
  threshold = 70,
  enabled = true,
}: UsePullToRefreshOptions) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const active = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const handleTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0].clientY;
      active.current = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!active.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && window.scrollY === 0) {
        const damped = Math.min(dy * 0.5, threshold * 1.5);
        setPullDistance(damped);
      }
    };

    const handleTouchEnd = async () => {
      if (!active.current) return;
      active.current = false;
      const distance = pullDistance;
      startY.current = null;
      if (distance >= threshold && !refreshing) {
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [enabled, threshold, onRefresh, pullDistance, refreshing]);

  return { pullDistance, refreshing, threshold };
}
